import {
  MAPPED_CONTENT_TYPES,
  EXTRA_METADATA_TYPES,
  ANALYTICS_STORAGE_KEYS,
} from "../../../shared/constants/analytics";
import PlayerConstants from "../../../shared/constants/player";
import { getEpisodeInfo } from "../../../shared/analytics/helpers";
import { trackMediaStart } from "../../../shared/analytics/media";
import { getRegionalChannelNumber } from "../../../shared/utils/epg";
import { setSessionStorage } from "../../../shared/utils/sessionStorage";
const {
  PLAYER_TYPE: { VOD, LIVE, RECORDING },
} = PlayerConstants;

const getPageName = (isLivePlayer, programDetailsRef, programContent) => {
  return isLivePlayer ? programDetailsRef.current?.metadata?.title : programContent?.[0]?.metadata?.title;
};

const getContentId = (isLivePlayer, programDetailsRef, programContent) => {
  return isLivePlayer ? programDetailsRef.current?.metadata?.contentId : programContent?.[0]?.metadata?.contentId;
};

const getMappedContentType = (
  isVodPlayer,
  convivaContentSubType,
  isRecordingPlayer,
  isMR,
  isLookbackStream,
  isRestartedStream
) => {
  if (isVodPlayer) return convivaContentSubType;
  if (isRecordingPlayer) return isMR ? MAPPED_CONTENT_TYPES.LPVR : MAPPED_CONTENT_TYPES.CPVR;
  if (isLookbackStream) return MAPPED_CONTENT_TYPES.LOOKBACK;
  if (isRestartedStream.current) return MAPPED_CONTENT_TYPES.RESTART;
  return MAPPED_CONTENT_TYPES.LIVE;
};

const getAsset = (
  isLivePlayer,
  assetProps,
  mappedContentType,
  playbackType,
  regionalChannelNumber,
  isRecordingPlayer,
  programContent
) => {
  const asset = {
    isItemLive: isLivePlayer,
    ...assetProps,
    mappedContentType,
    playbackType,
    channelNumber: regionalChannelNumber,
  };
  if (isRecordingPlayer) asset.channel = { ...programContent?.[0]?.channel, ...programContent?.[0] };
  return asset;
};

const trackPageViewAndMediaStart = (
  isLivePlayer,
  isRecordingPlayer,
  assetProps,
  asset,
  mappedContentType,
  playbackType,
  userProfile,
  appProvider,
  isInHome,
  progressTime,
  trackPageView,
  resetIsPageViewTracked,
  pageName,
  contentId,
  episodeInfo,
  isDynatraceTrackingEnabled
) => {
  trackPageView({
    pageName,
    contentId,
    episodeInfo,
    extraMetadataList: [
      {
        type: EXTRA_METADATA_TYPES.MEDIA_CONTENT,
        asset,
      },
    ],
  });
  resetIsPageViewTracked();
  if (isLivePlayer) {
    trackMediaStart(
      {
        ...assetProps?.metadata,
        channel: assetProps?.channel,
        mappedContentType,
      },
      LIVE,
      userProfile,
      appProvider,
      isInHome,
      false,
      isDynatraceTrackingEnabled
    );
  } else {
    trackMediaStart(
      { ...assetProps?.metadata, channel: asset?.channel, mappedContentType, playbackType },
      isRecordingPlayer ? RECORDING : VOD,
      userProfile,
      appProvider,
      isInHome,
      !!progressTime,
      isDynatraceTrackingEnabled
    );
  }
};

export const triggerAnalyticsCalls = ({
  isLivePlayer,
  programDetailsRef,
  programContent,
  isVodPlayer,
  convivaContentSubType,
  isRecordingPlayer,
  isMR,
  isLookbackStream,
  isRestartedStream,
  playbackType,
  appProvider,
  userProfile,
  isInHome,
  progressTime,
  trackPageView,
  resetIsPageViewTracked,
  isProgramChangedRef,
  isSwitchChannelRef,
  isInitMediaSessionTriggeredRef,
  isInitMediaSessionTriggeredTimer,
  isDynatraceTrackingEnabled,
}) => {
  const pageName = getPageName(isLivePlayer, programDetailsRef, programContent);
  const contentId = getContentId(isLivePlayer, programDetailsRef, programContent);
  const episodeInfo = getEpisodeInfo(isLivePlayer ? programDetailsRef.current : programContent?.[0]);
  const assetProps = isLivePlayer ? programDetailsRef.current : programContent?.[0];
  const mappedContentType = getMappedContentType(
    isVodPlayer,
    convivaContentSubType,
    isRecordingPlayer,
    isMR,
    isLookbackStream,
    isRestartedStream
  );
  const regionalChannelNumber = getRegionalChannelNumber(assetProps?.channel?.item, appProvider.channelMapID);
  const asset = getAsset(
    isLivePlayer,
    assetProps,
    mappedContentType,
    playbackType,
    regionalChannelNumber,
    isRecordingPlayer,
    programContent
  );

  trackPageViewAndMediaStart(
    isLivePlayer,
    isRecordingPlayer,
    assetProps,
    asset,
    mappedContentType,
    playbackType,
    userProfile,
    appProvider,
    isInHome,
    progressTime,
    trackPageView,
    resetIsPageViewTracked,
    pageName,
    contentId,
    episodeInfo,
    isDynatraceTrackingEnabled
  );

  isProgramChangedRef.current = false;
  if (isSwitchChannelRef.current) isSwitchChannelRef.current = false;
  if (!isInitMediaSessionTriggeredRef.current) {
    isInitMediaSessionTriggeredTimer.current = setTimeout(() => (isInitMediaSessionTriggeredRef.current = true), 2000);
  }
  setSessionStorage(ANALYTICS_STORAGE_KEYS.LINK, "");
};
